import React, { useCallback } from 'react';

import { useSharedPluginState } from '@atlaskit/editor-common/hooks';
import type { ToolbarUIComponentFactory } from '@atlaskit/editor-common/types';
import { useSharedPluginStateSelector } from '@atlaskit/editor-common/use-shared-plugin-state-selector';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';

// import { toggleProactiveAISuggestionDisplayWithAnalytics } from '../pm-plugins/ai-proactive/commands-with-analytics';

import type { AIProactivePluginInjectionAPI } from '../../aiProactivePluginType';
import { toggleProactiveAISuggestionDisplayWithAnalytics } from '../../pm-plugins/ai-proactive/commands-with-analytics';
import type { AIGlobalOptIn } from '../../types';
import { PrimaryToolbarButton } from '../components/PrimaryToolbarButton/PrimaryToolbarButton';

type PrimaryToolbarComponentType = {
	api: AIProactivePluginInjectionAPI;
	aiGlobalOptIn: AIGlobalOptIn;
	editorView: EditorView;
	disabled: boolean;
};

const PrimaryToolbarComponent = ({
	editorView,
	api,
	disabled,
	aiGlobalOptIn,
}: PrimaryToolbarComponentType) => {
	const { connectivityState } = useSharedPluginState(api, ['connectivity']);

	const isProactiveContextPanelOpen = useSharedPluginStateSelector(
		api,
		'aiProactive.isProactiveContextPanelOpen',
	);
	const showNewRecommendationsNotification = useSharedPluginStateSelector(
		api,
		'aiProactive.showNewRecommendationsNotification',
	);
	const unreadRecommendationCount = useSharedPluginStateSelector(
		api,
		'aiProactive.unreadRecommendationCount',
	);

	const onClick = useCallback(() => {
		// TODO: handle aiGlobalOptIn correctly
		const { state, dispatch } = editorView;
		toggleProactiveAISuggestionDisplayWithAnalytics(api?.analytics?.actions)('mainToolbar')(
			state,
			dispatch,
		);
	}, [api, editorView]);

	const showLoading = !isProactiveContextPanelOpen && !!showNewRecommendationsNotification;

	const isOffline = connectivityState?.mode === 'offline' || false;

	return (
		<>
			<PrimaryToolbarButton
				api={api}
				onClick={onClick}
				disabled={disabled || isOffline}
				selected={!!isProactiveContextPanelOpen}
				loading={showLoading}
				unreadCount={unreadRecommendationCount}
				view={editorView}
			/>
		</>
	);
};

export const getAIPrimaryToolbarComponent =
	({
		api,
		aiGlobalOptIn,
	}: {
		api: AIProactivePluginInjectionAPI;
		aiGlobalOptIn: AIGlobalOptIn;
	}): ToolbarUIComponentFactory =>
	({ editorView, disabled }) => {
		return (
			<PrimaryToolbarComponent
				editorView={editorView}
				api={api}
				aiGlobalOptIn={aiGlobalOptIn}
				disabled={disabled}
			/>
		);
	};
